import React, { useState, useEffect } from 'react';

const DocumentPricingDetails = ({ document }) => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Calculer les informations de tarification
  const items = document.items || [];
  
  const totalTarifTransporteur = items.reduce((sum, item) => sum + (item.tarif_transporteur_ht || 0) * (item.quantity || 1), 0);
  const totalMargeHt = items.reduce((sum, item) => sum + (item.marge_ht || 0) * (item.quantity || 1), 0);
  const totalMargeAvecTva = totalMargeHt * 1.18;
  const totalTarifClient = items.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 1), 0);
  
  const margeMovenPondere = totalTarifTransporteur > 0 ? 
    (totalMargeHt / totalTarifTransporteur) * 100 : 0;

  // Sprint 4: Charger les informations de paiement spécifiques au type de document
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/payment-info`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const info = await response.json();
          setPaymentInfo(info);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations de paiement:', error);
      } finally {
        setLoading(false);
      }
    };

    if (document.id) {
      fetchPaymentInfo();
    } else {
      setLoading(false);
    }
  }, [document.id]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        📊 Détails de Tarification
      </h3>
      
      {/* Vue d'ensemble */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Coût Transporteur</h4>
          <div className="text-2xl font-bold text-blue-600">
            {totalTarifTransporteur.toLocaleString()} CFA
          </div>
          <div className="text-sm text-blue-700">Coût d'achat total</div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-green-800 mb-2">Marge Réalisée</h4>
          <div className="text-2xl font-bold text-green-600">
            {totalMargeHt.toLocaleString()} CFA
          </div>
          <div className="text-sm text-green-700">
            {margeMovenPondere.toFixed(1)}% de marge moyenne
          </div>
        </div>
        
        <div className="bg-orange-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-orange-800 mb-2">Prix Client</h4>
          <div className="text-2xl font-bold text-orange-600">
            {document.total_ttc.toLocaleString()} CFA
          </div>
          <div className="text-sm text-orange-700">Prix final TTC</div>
        </div>
      </div>

      {/* Détail par item */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Détail par Item</h4>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Description</th>
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Qté</th>
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Tarif Transporteur</th>
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Marge % (sur coût)</th>
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Marge HT</th>
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Prix Client HT</th>
                <th className="border border-gray-300 p-2 text-left text-sm font-medium">Total TTC</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const itemMargePercent = item.tarif_transporteur_ht > 0 ? 
                  ((item.marge_ht || 0) / item.tarif_transporteur_ht) * 100 : 0;
                  
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 p-2 text-sm">
                      <div className="font-medium">{item.description}</div>
                      {item.ref && <div className="text-xs text-gray-500">Réf: {item.ref}</div>}
                    </td>
                    <td className="border border-gray-300 p-2 text-sm">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="border border-gray-300 p-2 text-sm">
                      <div className="font-medium text-blue-600">
                        {(item.tarif_transporteur_ht || 0).toLocaleString()} CFA
                      </div>
                      <div className="text-xs text-gray-500">
                        Total: {((item.tarif_transporteur_ht || 0) * (item.quantity || 1)).toLocaleString()} CFA
                      </div>
                    </td>
                    <td className="border border-gray-300 p-2 text-sm">
                      <div className="font-medium text-purple-600">
                        {itemMargePercent.toFixed(1)}%
                      </div>
                    </td>
                    <td className="border border-gray-300 p-2 text-sm">
                      <div className="font-medium text-green-600">
                        {(item.marge_ht || 0).toLocaleString()} CFA
                      </div>
                      <div className="text-xs text-gray-500">
                        Total: {((item.marge_ht || 0) * (item.quantity || 1)).toLocaleString()} CFA
                      </div>
                    </td>
                    <td className="border border-gray-300 p-2 text-sm">
                      <div className="font-medium text-orange-600">
                        {(item.unit_price || 0).toLocaleString()} CFA
                      </div>
                    </td>
                    <td className="border border-gray-300 p-2 text-sm">
                      <div className="font-medium text-green-700">
                        {(item.total_ttc || 0).toLocaleString()} CFA
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Résumé financier */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Résumé Financier</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Coût Transporteur HT:</span>
              <span className="text-sm font-medium">{totalTarifTransporteur.toLocaleString()} CFA</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Marge HT:</span>
              <span className="text-sm font-medium text-green-600">
                +{totalMargeHt.toLocaleString()} CFA
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Marge TTC:</span>
              <span className="text-sm font-medium text-green-700">
                +{totalMargeAvecTva.toLocaleString()} CFA
              </span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-gray-900">Prix Client HT:</span>
              <span className="text-sm font-medium">{totalTarifClient.toLocaleString()} CFA</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">TVA (18%):</span>
              <span className="text-sm font-medium">{(document.total_tva || 0).toLocaleString()} CFA</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-gray-900">Prix Client TTC:</span>
              <span className="text-sm font-bold text-green-600">{document.total_ttc.toLocaleString()} CFA</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Marge moyenne:</span>
              <span className="text-sm font-medium text-purple-600">{margeMovenPondere.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sprint 4: Informations de paiement adaptées au type de document */}
      {!loading && paymentInfo && paymentInfo.payment_enabled && (document.montant_paye || 0) > 0 && (
        <div className={`mt-4 p-4 rounded-lg ${
          document.type === 'facture' ? 'bg-green-50' : 'bg-blue-50'
        }`}>
          <h4 className={`text-md font-semibold mb-2 ${
            document.type === 'facture' ? 'text-green-900' : 'text-blue-900'
          }`}>
            {document.type === 'facture' ? 'Informations d\'Encaissement' : 'Informations de Paiement'}
            {document.type === 'bon_commande' && ' Transporteur'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className={`text-sm ${
                document.type === 'facture' ? 'text-green-700' : 'text-blue-700'
              }`}>
                {paymentInfo.montant_label}:
              </span>
              <div className={`text-lg font-bold ${
                document.type === 'facture' ? 'text-green-600' : 'text-blue-600'
              }`}>
                {(paymentInfo.montant_paye || 0).toLocaleString()} CFA
              </div>
            </div>
            <div>
              <span className={`text-sm ${
                document.type === 'facture' ? 'text-green-700' : 'text-blue-700'
              }`}>
                {paymentInfo.reste_label}:
              </span>
              <div className="text-lg font-bold text-red-600">
                {(paymentInfo.reste_a_payer || 0).toLocaleString()} CFA
              </div>
            </div>
            <div>
              <span className={`text-sm ${
                document.type === 'facture' ? 'text-green-700' : 'text-blue-700'
              }`}>
                Statut:
              </span>
              <div className="text-lg font-bold">
                {(paymentInfo.reste_a_payer || 0) <= 0 ? (
                  <span className="text-green-600">
                    {document.type === 'facture' ? 'Encaissé' : 'Payé'}
                  </span>
                ) : (paymentInfo.montant_paye || 0) > 0 ? (
                  <span className="text-yellow-600">
                    {document.type === 'facture' ? 'Partiellement encaissé' : 'Partiellement payé'}
                  </span>
                ) : (
                  <span className="text-red-600">
                    {document.type === 'facture' ? 'Non encaissé' : 'Non payé'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 4: Message pour les devis */}
      {!loading && paymentInfo && !paymentInfo.payment_enabled && (
        <div className="mt-4 bg-gray-50 p-4 rounded-lg">
          <h4 className="text-md font-semibold text-gray-900 mb-2">Informations de Paiement</h4>
          <p className="text-sm text-gray-600">
            ℹ️ La gestion des paiements n'est pas disponible pour les devis.
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentPricingDetails;